import React, {
    createContext,
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import { ApiError } from '@api/RestClient';
import { useDispatch } from 'react-redux';
import { projectsApi } from '@api/projectsApi';
import { isErrorResponse, isUuidString } from '@utils/utils';
import {
    addedProjectId,
    addedProjects,
    AllowedProjectModes,
} from '@redux/reducers/projectsReducer';
import { useNavigate, useParams } from 'react-router-dom';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectCurrentProjectId, selectProjectsById } from '@redux/selectors/selectors';
import { LoadingComponent } from '@components/LoadingComponent/LoadingComponent';
import ErrorComponent from '@components/ErrorComponent/ErrorComponent';
import { useToastContext } from './toastContext';
import { useTranslation } from 'react-i18next';
import { UseAllowedModes, useAllowedModes } from '@hooks/projects/useAllowedModes';
import { clearedSelectedMapFeatures } from '@redux/showInfo/showInfoReducer';
import { entityApi } from '@api/entityApi';
import * as schema from '@api/schema';
import { ProjectEntity } from '@api/entities';
import { TSchemaMapper } from '@utils/types';
import { DataById } from '@redux/map/types';

export enum ViewType {
    LINEAR,
    GRID,
}

export type ProjectState = {
    projectId?: string;
    loading: boolean;
    loadProjects: () => Promise<void>;
    updateProject: (
        id: string,
        data: TSchemaMapper<schema.definitions['entities_replancity_Project']>
    ) => Promise<schema.definitions['entities_replancity_Project']>;
    deleteProject: (id: string) => Promise<string>;
    error?: ApiError | undefined;
    projectsListViewType: ViewType;
    setProjectsListViewType: Dispatch<SetStateAction<ViewType>>;
    clearProjectData: () => void;
    projectPage: {
        pageId: string;
        readmeBlocks: schema.definitions['entities_replancity_ReadmeBlock'][];
    };
    getProjectPage: () => Promise<void>;
    cleanProjectPage: () => void;
    saveProjectPageBlock: ({
        entityName,
        pageId,
        ...block
    }: {
        entityName: string;
        pageId: string;
        runId?: string;
    } & Partial<schema.definitions['entities_replancity_ReadmeBlock']>) => Promise<
        schema.definitions['entities_replancity_ReadmeBlock']
    >;
} & UseAllowedModes;

const ProjectContext = createContext<ProjectState>({
    projectId: '',
    loading: false,
    loadProjects: () => {
        return Promise.resolve();
    },
    updateProject: (
        id: string,
        data: TSchemaMapper<schema.definitions['entities_replancity_Project']>
    ) => {
        return Promise.resolve({} as ProjectEntity);
    },
    deleteProject: (id: string) => {
        return Promise.resolve('');
    },
    projectModesLoaded: false,
    allowedModes: {} as AllowedProjectModes,
    mode: 'project',
    isModeAllowed: true,
    setProjectModesLoaded: () => false,
    loadProjectModes: () => {
        return Promise.resolve();
    },
    projectsListViewType: ViewType.LINEAR,
    setProjectsListViewType: () => ViewType.LINEAR,
    clearProjectData: () => {
        return;
    },
    projectPage: {
        pageId: '',
        readmeBlocks: [],
    },
    getProjectPage: () => {
        return Promise.resolve();
    },
    cleanProjectPage: () => {
        return;
    },
    saveProjectPageBlock: ({ entityName, pageId }: { entityName: string; pageId: string }) => {
        return Promise.resolve({});
    },
});

const useProjectContext = () => useContext<ProjectState>(ProjectContext);

const ProjectProvider = ({ children }: PropsWithChildren) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [projectsLoaded, setProjectsLoaded] = useState<boolean>(false);
    const [projectsListViewType, setProjectsListViewType] = useState<ViewType>(ViewType.LINEAR);
    const [projectPage, setProjectPage] = useState<{
        pageId: string;
        readmeBlocks: schema.definitions['entities_replancity_ReadmeBlock'][];
    }>({
        pageId: '',
        readmeBlocks: [],
    });
    const [projectsError, setProjectsError] = useState<ApiError>();
    const { projectId: projectIdParam } = useParams<{ projectId?: string }>();
    const projectsById = useTypedSelector(selectProjectsById);
    const currentProjectId = useTypedSelector(selectCurrentProjectId);

    const {
        projectModesLoaded,
        modesError,
        allowedModes,
        mode,
        isModeAllowed,
        setProjectModesLoaded,
        loadProjectModes,
    } = useAllowedModes();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { addToast } = useToastContext();
    const { t } = useTranslation();

    useEffect(() => {
        void (async function () {
            await loadProjects();
        })();
    }, []);

    useEffect(() => {
        if (projectIdParam && projectsLoaded) {
            if (!isUuidString(projectIdParam) || !projectsById[projectIdParam]) {
                addToast(`${t('projects-main.project-doesnt-exist')}`, 'info');
                navigate('/', { replace: true });
            }

            dispatch(addedProjectId(projectIdParam));

            if (!projectModesLoaded) {
                void (async function () {
                    await loadProjectModes(projectIdParam);
                })();
            }
        }
    }, [projectIdParam, projectsLoaded, projectsById]);

    const loadProjects = useCallback(async () => {
        const projects = await projectsApi.getProjects();

        if (isErrorResponse(projects)) {
            setProjectsError(projects);
        } else {
            //@ts-ignore
            const projectsById: DataById<ProjectEntity> = projects.reduce((acc, cur) => {
                acc[cur.id!] = cur;
                return acc;
            }, {});

            dispatch(addedProjects(projectsById));
        }

        setProjectsLoaded(true);
    }, []);

    const updateProject = useCallback(
        async (
            id: string,
            data: TSchemaMapper<schema.definitions['entities_replancity_Project']>
        ) => {
            const abortController = new AbortController();

            const project = projectsById[id];
            const resp = await entityApi.saveEntity<
                schema.definitions['entities_replancity_Project']
            >({
                entityName: project._entityName!,
                id: project.id,
                data,
                params: { responseView: 'api.projects-browse' },
                abortSignal: abortController.signal,
            });

            if (!isErrorResponse(resp)) {
                dispatch(addedProjects({ ...projectsById, [id]: resp as ProjectEntity }));
            }
            return resp;
        },
        [projectsById]
    );

    const getProjectPage = useCallback(async () => {
        const abortController = new AbortController();

        setLoading(true);
        const resp = await projectsApi.getProjectPage({
            projectId: projectIdParam!,
            abortSignal: abortController.signal,
        });
        if (!isErrorResponse(resp)) {
            if (!isErrorResponse(resp)) {
                const { id, readmeBlocks } = resp;
                setProjectPage({
                    pageId: id!,
                    readmeBlocks: readmeBlocks ?? [],
                });
            }
        }
        setLoading(false);
    }, [projectIdParam]);

    const cleanProjectPage = useCallback(() => {
        setProjectPage({
            pageId: '',
            readmeBlocks: [],
        });
    }, []);

    const saveProjectPageBlock = useCallback(
        async ({
            entityName,
            pageId,
            runId,
            ...block
        }: {
            entityName: string;
            pageId: string;
            runId?: string;
        } & schema.definitions['entities_replancity_ReadmeBlock']) => {
            const abortController = new AbortController();

            return await entityApi.saveEntity<
                Partial<schema.definitions['entities_replancity_ReadmeBlock']>
            >({
                entityName,
                id: block.id,
                data: {
                    readmePage: { id: pageId },
                    ...(runId ? { runTask: { id: runId } } : {}),
                    ...block,
                },
                abortSignal: abortController.signal,
            });
        },
        []
    );

    const deleteProject = async (id: string): Promise<string> => {
        return await projectsApi.deleteProject(id);
    };

    const clearProjectData = useCallback(() => {
        dispatch(addedProjectId());
        dispatch(clearedSelectedMapFeatures());
        setProjectModesLoaded(false);
    }, []);

    const value = {
        projectId: currentProjectId,
        loading,
        loadProjects,
        updateProject,
        deleteProject,
        projectModesLoaded,
        allowedModes,
        mode,
        isModeAllowed,
        setProjectModesLoaded,
        modesError,
        loadProjectModes,
        projectsListViewType,
        setProjectsListViewType,
        clearProjectData,
        projectPage,
        getProjectPage,
        cleanProjectPage,
        saveProjectPageBlock,
    };

    return (
        <ProjectContext.Provider value={value}>
            <LoadingComponent isLoading={!projectsLoaded}>
                <ErrorComponent error={projectsError}>{children}</ErrorComponent>
            </LoadingComponent>
        </ProjectContext.Provider>
    );
};

export { ProjectProvider, useProjectContext };
